import { type ComponentType, type ReactNode, useCallback, useEffect, useState } from "react";
import { match, P } from "ts-pattern";
import { FullscreenSpinner, YStack } from "@medbillai/ui";
import { back, createFlow, type FlowState, type FlowTransition, type OnDone, type Outcomes, type ScreenKey, type TaggedUnion, type TransitionTable, tagged, useBrowserHistorySync, useNavigableFlow } from "../../lib/flow-engine";
import { AccountCreated, type AccountCreatedOutcomes } from "./AccountCreated";
import { Education, type EducationOutcomes } from "./Education";
import { IntentFork, type IntentForkOutcomes } from "./IntentFork";
import { NotifPermission, type NotifPermissionOutcomes } from "./NotifPermission";
import type { Intent, IntentData, ResolvedIntentData } from "./types";
import { ValueProps, type ValuePropsOutcomes } from "./ValueProps";
export const ONBOARDING_VERSION = "v2.0" as const;
export type OnboardingConfig = {
  ValueProps: {
    needs: {};
    outcomes: ValuePropsOutcomes;
  };
  IntentFork: {
    needs: {};
    outcomes: IntentForkOutcomes;
  };
  Education: {
    needs: IntentData;
    outcomes: EducationOutcomes;
  };
  AccountCreated: {
    needs: {};
    outcomes: AccountCreatedOutcomes;
  };
  NotifPermission: {
    needs: ResolvedIntentData;
    outcomes: NotifPermissionOutcomes;
  };
  Complete: {
    needs: {};
    outcomes: never;
  };
};
export type OnboardingContext = {
  isWeb: boolean;
};
export const Screen = createFlow<OnboardingConfig>();

// Note: For auth handoff outcomes, we stay on the current screen to enable
// a seamless URL navigation animation.
const transitions: TransitionTable<OnboardingConfig, OnboardingContext> = {
  ValueProps: {
    logIn: () => Screen.ValueProps(),
    // Noop: Triggers exitToLogin onHandoff
    getStarted: () => Screen.IntentFork(),
    back: () => back()
  },
  IntentFork: {
    selected: ({
      intent
    }) => Screen.Education({
      intent
    }),
    back: () => back()
  },
  Education: {
    continue: () => Screen.Education(),
    // Noop: Triggers awaitingAccount onHandoff
    back: () => back()
  },
  AccountCreated: {
    ready: ({
      intent
    }, {
      isWeb
    }) => isWeb ? Screen.Complete() : Screen.NotifPermission({
      intent
    })
  },
  NotifPermission: {
    configured: () => Screen.Complete(),
    skip: () => Screen.Complete()
  },
  Complete: {}
};
type AuthHandoffShapes = {
  awaitingAccountCreation: {
    intent: Intent;
  };
  exitToLogin: {};
};
export const AuthHandoff = tagged<AuthHandoffShapes>();
export type AuthHandoff = TaggedUnion<AuthHandoffShapes>;
export type OnboardingTransitionProps = {
  stateKey: string;
  direction: 1 | -1;
  children: ReactNode;
};
export type OnboardingFlowProps = {
  initial: FlowState<OnboardingConfig>;
  ctx: OnboardingContext;
  onComplete: () => void;
  onAuthHandoff: OnDone<AuthHandoffShapes>;
  onTransition?: (event: FlowTransition<OnboardingConfig>) => void;
  /**
   * Screen-transition wrapper — supplied per-platform by the call site so
   * each platform uses the right animation tech (framer-motion on web,
   * Tamagui+Reanimated on native).
   */
  Transition: ComponentType<OnboardingTransitionProps>;
};
export function OnboardingFlow({
  initial,
  ctx,
  onAuthHandoff,
  onComplete,
  onTransition: parentOnTransition,
  Transition
}: OnboardingFlowProps) {
  const onTransition = useCallback((event: FlowTransition<OnboardingConfig>) => {
    match([event.from, event.outcome]).with([{
      tag: "ValueProps"
    }, {
      tag: "logIn"
    }], () => {
      onAuthHandoff(AuthHandoff.exitToLogin());
    }).with([{
      tag: "Education",
      data: {
        intent: P.select()
      }
    }, {
      tag: "continue"
    }], intent => {
      onAuthHandoff(AuthHandoff.awaitingAccountCreation({
        intent
      }));
    }).otherwise(() => {
      parentOnTransition?.(event);
    });
  }, [parentOnTransition, onAuthHandoff]);
  const [direction, setDirection] = useState<1 | -1>(1);
  const {
    state,
    bindOnDone: engineBindOnDone,
    canGoBack,
    goBack: engineGoBack
  } = useNavigableFlow<OnboardingConfig, OnboardingContext>({
    transitions,
    initial,
    ctx,
    onTransition
  });
  useEffect(() => {
    if (state.tag === "Complete") {
      onComplete();
    }
  }, [state.tag, onComplete]);

  // setState in this handler flushes synchronously when the handler returns,
  // re-rendering the outgoing screen with the new direction's `exit` prop.
  // The rAF then fires the engine dispatch in a separate batch, so when
  // AnimatePresence captures the exiting child's `exit`, direction has
  // already settled.
  const bindOnDone = <K extends ScreenKey<OnboardingConfig>,>(tag: K) => (outcome: TaggedUnion<Outcomes<OnboardingConfig, K>>) => {
    setDirection(outcome.tag === "back" ? -1 : 1);
    requestAnimationFrame(() => {
      engineBindOnDone(tag)(outcome);
    });
  };
  const goBack = () => {
    setDirection(-1);
    requestAnimationFrame(() => {
      engineGoBack();
    });
  };
  useBrowserHistorySync({
    stateTag: state.tag,
    canGoBack,
    goBack
  });
  const screen = match(state).with({
    tag: "ValueProps"
  }, () => <ValueProps onDone={bindOnDone("ValueProps")} />).with({
    tag: "IntentFork"
  }, () => <IntentFork onDone={bindOnDone("IntentFork")} />).with({
    tag: "Education"
  }, ({
    data
  }) => <Education intent={data.intent} onDone={bindOnDone("Education")} />).with({
    tag: "AccountCreated"
  }, () => <AccountCreated onDone={bindOnDone("AccountCreated")} />).with({
    tag: "NotifPermission"
  }, ({
    data
  }) => <NotifPermission intent={data.intent} onDone={bindOnDone("NotifPermission")} />).with({
    tag: "Complete"
  }, () => <FullscreenSpinner />).exhaustive();
  return <YStack flex={1} position="relative" overflow="hidden" data-sentry-element="YStack" data-sentry-component="OnboardingFlow" data-sentry-source-file="OnboardingFlow.tsx">
      <Transition stateKey={state.tag} direction={direction} data-sentry-element="Transition" data-sentry-source-file="OnboardingFlow.tsx">
        {screen}
      </Transition>
    </YStack>;
}